/**
 * Tabela wierszy z GET /ksef/otrzymane (kontrakt: backend/api/ksef_otrzymane.py).
 *
 * Kolumny: Numer (+ numer KSeF skrocony, pelny w tooltipie) · Data · Sprzedawca (nazwa + NIP, odznaka SFD) · Rodzaj ·
 * Netto · Brutto · Status (pigulka; powod pominiecia / blad XML / inna kwota w tooltipie) · W systemie (odnosnik do
 * /purchase-invoices/<id> z dopasowanie.faktura + sklep; wiersz kontroli faktur SFD) · Sugestia (towar/koszt/korekta) ·
 * Akcje: Podgląd | Dodaj jako fakturę zakupu (wiersz.mozna_dodac; modal z wyborem sklepu) | Pomiń (wiersz.mozna_pominac,
 * z powodem inline) / Przywróć | XML (wiersz.ma_xml).
 *
 * `AkcjeDodaj` (przycisk + modal) jest wspolny dla tabeli i stopki modalu szczegolow.
 * Kontener ma overflow: auto - tabela nie rozpycha strony.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ksefOtrzymaneService } from '../../services/ksefOtrzymaneService';
import DodajFaktureModal from './DodajFaktureModal';
import {
  STATUSY, RODZAJE, SUGESTIE, formatujDate, formatujKwote, skrocNumerKsef, skroc, komunikatBledu,
  fakturaWSystemie, tooltipStatusu,
} from './format';

const KOLUMNY = ['Numer', 'Data', 'Sprzedawca', 'Rodzaj', 'Netto', 'Brutto', 'Status', 'W systemie', 'Sugestia', 'Akcje'];
const th = (tekst, extra = {}) => (
  <th key={tekst} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', ...extra }}>{tekst}</th>
);
const td = { padding: '5px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: '11px' };

const btnMaly = (tlo, kolor, ramka) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap',
  border: `1px solid ${ramka}`, background: tlo, color: kolor,
});
const pole = { padding: '4px 8px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };

const Pigulka = ({ testId, etykieta, kolor, tlo, title }) => (
  <span data-testid={testId} title={title || undefined}
    style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: kolor, backgroundColor: tlo }}>
    {etykieta}
  </span>
);

export const ZnacznikStatusu = ({ wiersz }) => {
  const s = STATUSY[wiersz?.status] || { etykieta: wiersz?.status || '—', kolor: 'var(--text-secondary)', tlo: 'var(--bg)' };
  return <Pigulka testId="status" etykieta={s.etykieta} kolor={s.kolor} tlo={s.tlo} title={tooltipStatusu(wiersz)} />;
};

export const ZnacznikRodzaju = ({ rodzaj }) => {
  const r = RODZAJE[rodzaj] || { etykieta: rodzaj || '—', pelna: rodzaj || '', kolor: 'var(--text-secondary)', tlo: 'var(--bg)' };
  return <Pigulka testId="rodzaj" etykieta={r.etykieta} kolor={r.kolor} tlo={r.tlo} title={r.pelna} />;
};

/** Sugestia backendu: 'zakup' -> „towar (do magazynu)”, 'koszt' -> „koszt (księgowość)”, 'korekta' -> „korekta”. */
export const ZnacznikSugestii = ({ sugestia }) => {
  const s = SUGESTIE[sugestia];
  if (!s) return <span data-testid="sugestia" style={{ color: 'var(--text-muted)' }}>—</span>;
  return <Pigulka testId="sugestia" etykieta={s.etykieta} kolor={s.kolor} tlo={s.tlo} title={s.opis} />;
};

/** Pobranie XML faktury z KSeF jako plik (GET /<id>/xml jako blob, z sesja). */
export const pobierzXmlDoPliku = async (wiersz, pobierz = ksefOtrzymaneService.pobierzXml) => {
  try {
    const blob = await pobierz(wiersz.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(wiersz.ksef_numer || wiersz.numer_faktury || `ksef-${wiersz.id}`).replace(/[^\w.-]+/g, '_')}.xml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (err) {
    if (!err?.isAuthError) toast.error(komunikatBledu(err, 'Nie udało się pobrać XML.'));
  }
};

/**
 * "Dodaj jako fakturę zakupu" -> modal z wyborem sklepu (wspolne dla tabeli i modalu szczegolow).
 * Widoczny, gdy backend mowi `mozna_dodac` (status nowa/blad + XML + nie korekta); `widoczny` nadpisuje (modal szczegolow
 * ma mozna_dodac zaostrzone o pokrycie pozycji). `brakujace` (ze szczegolow) = przycisk przygaszony z tooltipem o brakach.
 */
export const AkcjeDodaj = ({ wiersz, sklepy, onDodano, kompakt = true, widoczny, brakujace }) => {
  const [otwarty, setOtwarty] = useState(false);
  const pokaz = widoczny !== undefined ? Boolean(widoczny) : Boolean(wiersz?.mozna_dodac);
  if (!pokaz) return null;
  const braki = Array.isArray(brakujace) ? brakujace.length : 0;
  const styl = kompakt
    ? btnMaly('var(--success)', '#fff', 'var(--success)')
    : { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--success)', background: 'var(--success)', color: '#fff' };
  return (
    <>
      <button type="button" data-testid="btn-dodaj" onClick={() => setOtwarty(true)}
        title={braki > 0
          ? `Brakuje ${braki} ${braki === 1 ? 'produktu' : 'produktów'} w systemie — backend odmówi (BRAK_POKRYCIA), modal pokaże listę braków`
          : 'Dodaj tę fakturę jako fakturę zakupu (pozycje z XML KSeF, produkty muszą być w systemie)'}
        style={{ ...styl, opacity: braki > 0 ? 0.75 : 1 }}>
        {kompakt ? 'Dodaj' : 'Dodaj jako fakturę zakupu'}
      </button>
      {otwarty && (
        <DodajFaktureModal wiersz={wiersz} sklepy={sklepy} onZamknij={() => setOtwarty(false)}
          onDodano={(wynik, w) => { if (onDodano) onDodano(wynik, w); }} />
      )}
    </>
  );
};

/** "Pomiń" z powodem (inline) -> POST /<id>/pomin (wiersz.mozna_pominac); "Przywróć" -> POST /<id>/przywroc (status pominieta). */
export const AkcjePomin = ({ wiersz, onZmiana }) => {
  const [pomijanie, setPomijanie] = useState(false);
  const [powod, setPowod] = useState('');
  const [wysylanie, setWysylanie] = useState(false);

  const wykonaj = async (fn, komunikat) => {
    setWysylanie(true);
    try {
      const dane = await fn();
      toast.success(komunikat);
      setPomijanie(false);
      if (onZmiana) onZmiana(dane, wiersz);
    } catch (err) {
      if (!err?.isAuthError) toast.error(komunikatBledu(err));
    } finally {
      setWysylanie(false);
    }
  };

  if (wiersz.status === 'pominieta') {
    return (
      <button type="button" data-testid="btn-przywroc" disabled={wysylanie}
        onClick={() => wykonaj(() => ksefOtrzymaneService.przywroc(wiersz.id), `Przywrócono fakturę ${wiersz.numer_faktury}`)}
        style={btnMaly('var(--card-bg)', 'var(--primary)', 'var(--primary)')}>
        Przywróć
      </button>
    );
  }
  if (!wiersz.mozna_pominac) return null;
  if (pomijanie) {
    return (
      <span data-testid="pomin-formularz" style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" aria-label="Powód pominięcia" value={powod} onChange={(e) => setPowod(e.target.value)} placeholder="Powód pominięcia" maxLength={300}
          disabled={wysylanie} style={{ ...pole, minWidth: '160px' }} />
        <button type="button" data-testid="btn-pomin-potwierdz" disabled={wysylanie}
          onClick={() => wykonaj(() => ksefOtrzymaneService.pomin(wiersz.id, powod.trim()), `Pominięto fakturę ${wiersz.numer_faktury}`)}
          style={btnMaly('var(--text-secondary)', '#fff', 'var(--text-secondary)')}>
          {wysylanie ? 'Pomijam…' : 'Pomiń'}
        </button>
        <button type="button" onClick={() => setPomijanie(false)} disabled={wysylanie} style={btnMaly('var(--card-bg)', 'var(--text)', 'var(--border)')}>Anuluj</button>
      </span>
    );
  }
  return (
    <button type="button" data-testid="btn-pomin" onClick={() => setPomijanie(true)} title="Pomiń tę fakturę (z powodem) — nie będzie liczona jako nowa do rozliczenia"
      style={btnMaly('var(--card-bg)', 'var(--text-secondary)', 'var(--border)')}>
      Pomiń
    </button>
  );
};

/** "XML" - pobranie pliku, gdy wiersz.ma_xml; bez XML: tooltip z bledem pobierania albo "XML czeka". */
export const AkcjeXml = ({ wiersz, onXml }) => {
  if (!wiersz?.ma_xml) {
    return wiersz?.xml_blad
      ? <span data-testid="xml-blad" title={wiersz.xml_blad} style={{ color: 'var(--danger)', fontSize: '10px', whiteSpace: 'nowrap' }}>brak XML</span>
      : <span data-testid="xml-czeka" title="XML zostanie pobrany przy kolejnej synchronizacji" style={{ color: 'var(--text-muted)', fontSize: '10px', whiteSpace: 'nowrap' }}>XML czeka</span>;
  }
  return (
    <button type="button" data-testid="btn-xml" onClick={() => (onXml || pobierzXmlDoPliku)(wiersz)}
      title="Pobierz XML faktury z KSeF (FA(3))"
      style={btnMaly('var(--bg)', 'var(--text-primary)', 'var(--border)')}>
      XML
    </button>
  );
};

/** Kolumna "Akcje": Podgląd | Dodaj | Pomiń / Przywróć | XML. */
export const AkcjeKsef = ({ wiersz, sklepy, onPodglad, onDodano, onZmiana, onXml }) => (
  <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
    <button type="button" data-testid="btn-podglad" onClick={() => onPodglad && onPodglad(wiersz)}
      style={btnMaly('var(--card-bg)', 'var(--primary)', 'var(--primary)')}>
      Podgląd
    </button>
    <AkcjeDodaj wiersz={wiersz} sklepy={sklepy} onDodano={onDodano} />
    <AkcjePomin wiersz={wiersz} onZmiana={onZmiana} />
    <AkcjeXml wiersz={wiersz} onXml={onXml} />
  </span>
);

/**
 * Kolumna "W systemie": odnosnik do faktury zakupu (faktura_zakupowa_id + dopasowanie.faktura {numer, sklep}),
 * ostrzezenie "inna kwota" (dopasowanie.kwota_zgodna === false), wiersz kontroli faktur SFD (dopasowanie.kontrola).
 */
export const WSystemie = ({ wiersz }) => {
  const f = fakturaWSystemie(wiersz);
  const k = wiersz?.dopasowanie?.kontrola || null;
  if (!f && !k) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span data-testid="w-systemie" style={{ display: 'inline-block', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
      {f && (f.id
        ? <Link to={`/purchase-invoices/${f.id}`} title={`Otwórz fakturę #${f.id}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>{f.numer || `#${f.id}`}</Link>
        : <span style={{ fontWeight: 600 }}>{f.numer}</span>)}
      {f?.kwota_zgodna === false && <span data-testid="inna-kwota" title="Kwota brutto faktury w systemie różni się od kwoty z KSeF" style={{ marginLeft: '4px', fontSize: '10px', color: 'var(--warning)', fontWeight: 600 }}>inna kwota</span>}
      {f?.sklep && <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)' }}>{f.sklep}</span>}
      {k && (
        <span data-testid="kontrola-sfd" title={`Wiersz Kontroli faktur SFD #${k.id}${k.plik_format === 'fa3' ? ' — XML z KSeF podpięty jako plik dokumentu' : ''}`}
          style={{ display: 'block', fontSize: '10px', color: 'var(--info)' }}>
          kontrola SFD #{k.id}{k.plik_format === 'fa3' ? ' · plik z KSeF' : ''}
        </span>
      )}
    </span>
  );
};

/**
 * Props: wiersze, ladowanie, sklepy (do modalu dodawania), onPodglad(wiersz), onDodano(wynik, wiersz),
 * onZmiana(wynik, wiersz) (pomin/przywroc), onXml(wiersz) (opcjonalnie - domyslnie pobranie pliku).
 */
const TabelaKsef = ({ wiersze, ladowanie, sklepy, onPodglad, onDodano, onZmiana, onXml }) => {
  const lista = Array.isArray(wiersze) ? wiersze : [];
  return (
    <div data-testid="tabela-ksef" style={{ overflow: 'auto', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 1 }}>
          <tr>
            {KOLUMNY.map(k => th(k, (k === 'Netto' || k === 'Brutto') ? { textAlign: 'right' } : {}))}
          </tr>
        </thead>
        <tbody>
          {lista.map((w, idx) => (
            <tr key={`${w.id}-${idx}`} data-testid="wiersz-ksef"
              style={{ backgroundColor: w.status === 'pominieta' ? 'var(--bg)' : (idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)'), opacity: w.status === 'pominieta' ? 0.75 : 1 }}>
              <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>
                {w.numer_faktury || '—'}
                <span data-testid="numer-ksef" title={w.ksef_numer || undefined} style={{ display: 'block', fontSize: '10px', fontWeight: 400, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                  {w.ksef_numer ? `KSeF ${skrocNumerKsef(w.ksef_numer)}` : 'bez numeru KSeF'}
                </span>
                {w.korekta && w.numer_korygowanej && (
                  <span data-testid="numer-korygowanej" title={w.ksef_numer_korygowanej ? `KSeF faktury korygowanej: ${w.ksef_numer_korygowanej}` : undefined}
                    style={{ display: 'block', fontSize: '10px', fontWeight: 400, color: 'var(--orange)' }}>
                    do {w.numer_korygowanej}
                  </span>
                )}
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap' }} title={w.data_nadania_ksef ? `nadano do KSeF: ${formatujDate(w.data_nadania_ksef)}` : undefined}>{formatujDate(w.data_wystawienia)}</td>
              <td style={{ ...td, maxWidth: '220px' }} title={w.sprzedawca_nazwa || undefined}>
                <span style={{ fontWeight: 600 }}>{skroc(w.sprzedawca_nazwa, 36) || '—'}</span>
                {w.sfd && <span data-testid="sfd" title="Dostawca towarowy SFD" style={{ marginLeft: '4px', fontSize: '10px', padding: '0 5px', borderRadius: '8px', color: 'var(--info)', backgroundColor: 'var(--info-light)', fontWeight: 600 }}>SFD</span>}
                {w.sprzedawca_nip && <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)' }}>NIP {w.sprzedawca_nip}</span>}
              </td>
              <td style={td}><ZnacznikRodzaju rodzaj={w.rodzaj} /></td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatujKwote(w.netto)}</td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--orange)' }}>{formatujKwote(w.brutto)}{w.waluta && w.waluta !== 'PLN' ? ` ${w.waluta}` : ''}</td>
              <td style={td}><ZnacznikStatusu wiersz={w} /></td>
              <td style={td}><WSystemie wiersz={w} /></td>
              <td style={td}><ZnacznikSugestii sugestia={w.sugestia} /></td>
              <td style={td}><AkcjeKsef wiersz={w} sklepy={sklepy} onPodglad={onPodglad} onDodano={onDodano} onZmiana={onZmiana} onXml={onXml} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {!ladowanie && lista.length === 0 && (
        <div data-testid="pusta-lista" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak faktur dla wybranych filtrów
        </div>
      )}
      {ladowanie && <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie…</div>}
    </div>
  );
};

export default TabelaKsef;
